import React, { memo, useState, useMemo, useEffect, useRef } from 'react';
import { Contact, ContactStore } from '../stores/ContactStore';
import { SortField, SortDirection } from '../../components/ColumnFilters';
import { List, RowComponentProps } from 'react-window';
import { ListFieldType } from '../../components/ListCustomizer';

interface ContactListViewProps {
  contacts: Contact[];
  title: string;
  maxHeight?: string;
  contactStore: ContactStore;
  columnFilters?: {
    company: string;
    lastName: string;
    firstName: string;
    email: string;
  };
  sortField?: SortField | null;
  sortDirection?: SortDirection;
  customFieldOrder?: ListFieldType[];
}

const ContactListView = memo<ContactListViewProps>(({ 
  contacts, 
  title, 
  maxHeight = "max-h-96",
  contactStore,
  columnFilters = { company: '', lastName: '', firstName: '', email: '' },
  sortField = null,
  sortDirection = 'asc',
  customFieldOrder
}) => {
  const [selectionVersion, setSelectionVersion] = useState(0);
  const listRef = useRef<any>(null);

  const fieldOrder = useMemo<ListFieldType[]>(() => {
    if (customFieldOrder) return customFieldOrder;
    try {
      const saved = localStorage.getItem('list.customOrder');
      if (saved) return JSON.parse(saved) as ListFieldType[];
    } catch {}
    return ['company', 'lastName', 'firstName', 'email', 'source', 'none'];
  }, [customFieldOrder]);

  // Calculate number of active field columns (visible columns only, equal width)
  const activeFieldCount = useMemo(() => {
    return fieldOrder.filter(f => f !== 'none').length;
  }, [fieldOrder]);
  
  // Grid template: fixed-width checkbox (auto) + equal-width field columns
  const gridTemplateColumns = useMemo(() => {
    return `auto repeat(${activeFieldCount}, minmax(0, 1fr))`;
  }, [activeFieldCount]);

  // Reset list when field order changes
  useEffect(() => {
    setSelectionVersion(prev => prev + 1);
    if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
      listRef.current.resetAfterIndex(0);
    }
  }, [fieldOrder]);

  // Listen for selection changes to force re-render
  useEffect(() => {
    const handleSelectionChange = () => {
      setSelectionVersion(prev => prev + 1);
      if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
        listRef.current.resetAfterIndex(0);
      }
    };

    contactStore.addSelectionListener(handleSelectionChange);
    return () => {
      contactStore.removeSelectionListener(handleSelectionChange);
    };
  }, [contactStore]);

  const handleContactSelect = (contactId: string) => {
    if (!contactId) return;
    contactStore.toggleContactSelection(contactId);
  };

  const handleSelectAll = () => {
    const allSelected = filteredAndSortedContacts.every(contact => contactStore.isContactSelected(contact.id));
    if (allSelected) {
      filteredAndSortedContacts.forEach(contact => contactStore.deselectContact(contact.id));
    } else {
      filteredAndSortedContacts.forEach(contact => contactStore.selectContact(contact.id));
    }
  };

  // Filter and sort contacts based on current filter and sort settings
  const filteredAndSortedContacts = useMemo(() => {
    // First filter contacts based on column filters
    let filteredContacts = contacts.filter(contact => {
      const companyMatch = !columnFilters.company || 
        (contact.company || '').toLowerCase().includes(columnFilters.company.toLowerCase());
      
      const lastNameMatch = !columnFilters.lastName || 
        (contact.surname || '').toLowerCase().includes(columnFilters.lastName.toLowerCase());
      
      const firstNameMatch = !columnFilters.firstName || 
        (contact.givenName || '').toLowerCase().includes(columnFilters.firstName.toLowerCase());
      
      const emailMatch = !columnFilters.email || 
        (contact.emailPrimary || '').toLowerCase().includes(columnFilters.email.toLowerCase());
      
      return companyMatch && lastNameMatch && firstNameMatch && emailMatch;
    });

    // Then sort if sort field is specified
    if (!sortField) return filteredContacts;

    return [...filteredContacts].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortField) {
        case 'company':
          aValue = a.company || '';
          bValue = b.company || '';
          break;
        case 'firstName':
          aValue = a.givenName || '';
          bValue = b.givenName || '';
          break;
        case 'lastName':
          aValue = a.surname || '';
          bValue = b.surname || '';
          break;
        case 'email':
          aValue = a.emailPrimary || '';
          bValue = b.emailPrimary || '';
          break;
        case 'source':
          aValue = a.sourceType;
          bValue = b.sourceType;
          break;
        default:
          return 0;
      }

      // Use localeCompare with Latvian locale for proper sorting of special characters
      // Fallback to English for non-Latvian letters (Q, W, X, Y)
      const comparison = aValue.localeCompare(bValue, ['lv', 'en'], { sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [contacts, sortField, sortDirection, columnFilters.company, columnFilters.lastName, columnFilters.firstName, columnFilters.email]);

  const getItemSize = () => 40; // Row height in pixels

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {title} ({filteredAndSortedContacts.length.toLocaleString()})
          {filteredAndSortedContacts.length !== contacts.length && (
            <span className="text-sm text-gray-500 ml-2">
              of {contacts.length.toLocaleString()}
            </span>
          )}
        </h3>
      </div>
      <div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden w-full">
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200">
            {/* Column Names Row */}
            <div className={`grid gap-2 sm:gap-4 px-2 sm:px-4 py-3 text-sm font-medium text-gray-700`} style={{ gridTemplateColumns }}>
              <div className="w-auto">
                <input
                  type="checkbox"
                  checked={filteredAndSortedContacts.length > 0 && filteredAndSortedContacts.every(contact => contactStore.isContactSelected(contact.id))}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  aria-label="Select all contacts"
                />
              </div>
              {fieldOrder.filter(f => f !== 'none').map((field) => (
                <div
                  key={field}
                  className={`truncate ${field === 'source' ? 'text-center' : ''}`}
                >
                  {field === 'company' && 'Company'}
                  {field === 'lastName' && 'Last Name'}
                  {field === 'firstName' && 'First Name'}
                  {field === 'email' && 'Email'}
                  {field === 'source' && 'Source'}
                  {field === 'phone' && 'Phone'}
                </div>
              ))}
            </div>
          </div>
          
          {/* Virtualized Table Body */}
          <div className="h-[550px]">
            <List
              listRef={listRef}
              rowCount={filteredAndSortedContacts.length}
              rowHeight={getItemSize}
              rowComponent={({ index, style }: RowComponentProps) => {
                const contact = filteredAndSortedContacts[index];
                if (!contact?.id) return null;
                const isSelected = contactStore.isContactSelected(contact.id);
                const isInEmail = contactStore.isContactInEmail(contact);
                
                // eslint-disable-next-line react/forbid-dom-props
                return (
                  <div
                    style={{ ...style, gridTemplateColumns }}
                    key={contact.id}
                    className={`grid gap-2 sm:gap-4 px-2 sm:px-4 py-2 cursor-pointer transition-colors border-b border-gray-200 ${
                      isInEmail 
                        ? 'bg-gray-200 opacity-60' 
                        : isSelected 
                          ? 'bg-blue-50' 
                          : 'hover:bg-gray-50'
                    }`}
                    onClick={() => !isInEmail && handleContactSelect(contact.id)}
                  >
                    <div className="w-auto flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isInEmail}
                        onChange={() => !isInEmail && handleContactSelect(contact.id)}
                        className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                          isInEmail ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${contact.displayName}`}
                      />
                    </div>
                    {fieldOrder.filter(f => f !== 'none').map((field) => {
                      if (field === 'company') {
                        return (
                          <div key={field} className="flex items-center text-sm text-gray-900 truncate" title={contact.company || '-' }>
                            {contact.company || '-'}
                          </div>
                        );
                      }
                      if (field === 'lastName') {
                        return (
                          <div key={field} className="flex items-center text-sm text-gray-900 truncate" title={contact.surname || '-' }>
                            {contact.surname || '-'}
                          </div>
                        );
                      }
                      if (field === 'firstName') {
                        return (
                          <div key={field} className="flex items-center text-sm text-gray-900 truncate" title={contact.givenName || '-' }>
                            {contact.givenName || '-'}
                          </div>
                        );
                      }
                      if (field === 'email') {
                        return (
                          <div key={field} className="flex items-center text-xs text-blue-600 truncate" title={contact.emailPrimary || '-' }>
                            {contact.emailPrimary || '-'}
                          </div>
                        );
                      }
                      if (field === 'phone') {
                        const primaryPhone = contact.phones && contact.phones.length > 0 ? contact.phones[0] : null;
                        const phoneDisplay = primaryPhone ? primaryPhone.number : '-';
                        return (
                          <div key={field} className="flex items-center text-xs text-gray-900 truncate" title={phoneDisplay}>
                            {phoneDisplay}
                          </div>
                        );
                      }
                      // source
                      return (
                        <div key={field} className="flex items-center justify-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            contact.sourceType === 'personal' 
                              ? 'bg-green-100 text-green-800' 
                              : contact.sourceType === 'internal'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {contact.sourceType === 'personal' ? 'Personal' : 
                             contact.sourceType === 'internal' ? 'Internal' : 'Corporate'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
              rowProps={{}}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

ContactListView.displayName = 'ContactListView';

export default ContactListView;
